import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { descobrirModelos, listarProvedores, removerProvedor, salvarProvedor, testarProvedor } from '../lib/api';
import { adminAutenticado, autenticarAdmin, sairAdmin } from '../lib/adminAuth';
import type { ModeloAdministrativo, ModoJogo, ProtocoloModelo, ProvedorAdministrativo, TipoAutenticacao, TipoProvedor } from '../types';
import { Icone } from './Icone';

interface Props {
  aoVoltar: () => void;
  aoAbrirLab?: (modo: ModoJogo) => void;
  aoAbrirBenchessmark?: () => void;
}

interface Preset {
  tipo: TipoProvedor;
  nome: string;
  descricao: string;
  baseUrl: string;
  caminhoModelos: string;
  caminhoGeracao: string;
  autenticacao: TipoAutenticacao;
}

const presets: Preset[] = [
  { tipo: 'minimax-openai', nome: 'MiniMax · formato OpenAI', descricao: 'Catálogo e geração em formato OpenAI.', baseUrl: 'https://api.minimax.io/v1', caminhoModelos: '/models', caminhoGeracao: '/chat/completions', autenticacao: 'bearer' },
  { tipo: 'minimax-anthropic', nome: 'MiniMax · formato Anthropic', descricao: 'Mensagens no formato Anthropic.', baseUrl: 'https://api.minimax.io/anthropic', caminhoModelos: '/v1/models', caminhoGeracao: '/v1/messages', autenticacao: 'x-api-key' },
  { tipo: 'stepfun', nome: 'StepFun · Step Plan', descricao: 'Modelos rápidos do plano StepFun.', baseUrl: 'https://api.stepfun.ai/step_plan/v1', caminhoModelos: '/models', caminhoGeracao: '/chat/completions', autenticacao: 'bearer' },
  { tipo: 'opencode-zen', nome: 'OpenCode Zen', descricao: 'Catálogo multiformato com protocolo por modelo.', baseUrl: 'https://opencode.ai/zen/v1', caminhoModelos: '/models', caminhoGeracao: '/chat/completions', autenticacao: 'bearer' },
  { tipo: 'opencode-go', nome: 'OpenCode Go', descricao: 'Seleção Go com modelos em mais de um formato.', baseUrl: 'https://opencode.ai/zen/go/v1', caminhoModelos: '/models', caminhoGeracao: '/chat/completions', autenticacao: 'bearer' },
  { tipo: 'llamacpp', nome: 'llama.cpp local', descricao: 'Descoberta automática no endereço local, porta 8085.', baseUrl: 'http://127.0.0.1:8085/v1', caminhoModelos: '/models', caminhoGeracao: '/chat/completions', autenticacao: 'nenhuma' },
  { tipo: 'personalizado', nome: 'Provedor personalizado', descricao: 'Configure um serviço compatível por conta própria.', baseUrl: '', caminhoModelos: '/models', caminhoGeracao: '/chat/completions', autenticacao: 'bearer' }
];

const protocoloNome: Record<ProtocoloModelo, string> = {
  'openai-chat': 'Conversa no formato OpenAI',
  'openai-responses': 'Respostas no formato OpenAI',
  anthropic: 'Mensagens no formato Anthropic',
  google: 'Conteúdo no formato Google'
};

const tipoNome: Record<TipoProvedor, string> = {
  'minimax-openai': 'MiniMax · OpenAI',
  'minimax-anthropic': 'MiniMax · Anthropic',
  stepfun: 'StepFun',
  'opencode-zen': 'OpenCode Zen',
  'opencode-go': 'OpenCode Go',
  llamacpp: 'llama.cpp local',
  personalizado: 'Personalizado'
};

function ordenarModelos(modelos: ModeloAdministrativo[]) {
  return [...modelos].sort(
    (a, b) => Number(b.padrao) - Number(a.padrao) || Number(b.liberado) - Number(a.liberado) || a.nome.localeCompare(b.nome, 'pt-BR')
  );
}

export function Administracao({ aoVoltar, aoAbrirLab, aoAbrirBenchessmark }: Props) {
  const [liberado, setLiberado] = useState(() => adminAutenticado());
  const [senha, setSenha] = useState('');
  const [erroSenha, setErroSenha] = useState('');

  const [provedores, setProvedores] = useState<ProvedorAdministrativo[]>([]);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [edicao, setEdicao] = useState<ProvedorAdministrativo | null>(null);
  const [chave, setChave] = useState('');
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [operacao, setOperacao] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);
  const [adicionar, setAdicionar] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);
  const [modeloManual, setModeloManual] = useState('');

  useEffect(() => {
    if (!liberado) return;
    void carregar();
  }, [liberado]);

  function entrar(e?: FormEvent) {
    e?.preventDefault();
    if (autenticarAdmin(senha)) {
      setLiberado(true);
      setSenha('');
      setErroSenha('');
      return;
    }
    setErroSenha('Senha incorreta.');
  }

  function sair() {
    sairAdmin();
    setLiberado(false);
    aoVoltar();
  }

  async function carregar() {
    setCarregando(true);
    try {
      const lista = await listarProvedores();
      setProvedores(lista);
      const id = selecionado && lista.some((p) => p.id === selecionado) ? selecionado : lista[0]?.id ?? null;
      setSelecionado(id);
      setEdicao(lista.find((p) => p.id === id) ?? null);
    } catch (e) {
      setMensagem({ tipo: 'erro', texto: e instanceof Error ? e.message : 'Não foi possível carregar a administração.' });
    } finally {
      setCarregando(false);
    }
  }

  function escolher(p: ProvedorAdministrativo) {
    setSelecionado(p.id);
    setEdicao(structuredClone(p));
    setChave('');
    setBusca('');
    setMensagem(null);
  }

  async function salvar(atual?: ProvedorAdministrativo) {
    const alvo = atual ?? edicao;
    if (!alvo) return;
    setOperacao('salvar');
    setMensagem(null);
    try {
      const salvo = await salvarProvedor({ ...alvo, chave: chave || undefined });
      setProvedores((lista) => lista.map((p) => (p.id === salvo.id ? salvo : p)));
      setEdicao(structuredClone(salvo));
      setSelecionado(salvo.id);
      setChave('');
      setMensagem({ tipo: 'sucesso', texto: 'Configuração salva com proteção neste computador.' });
      return salvo;
    } catch (e) {
      setMensagem({ tipo: 'erro', texto: e instanceof Error ? e.message : 'Não foi possível salvar.' });
    } finally {
      setOperacao(null);
    }
    return null;
  }

  async function descobrir() {
    if (!edicao) return;
    const salvo = await salvar();
    if (!salvo) return;
    setOperacao('descobrir');
    setMensagem(null);
    try {
      const atualizado = await descobrirModelos(salvo.id);
      setProvedores((lista) => lista.map((p) => (p.id === atualizado.id ? atualizado : p)));
      setEdicao(structuredClone(atualizado));
      setMensagem({ tipo: 'sucesso', texto: `${atualizado.modelos.length} modelo(s) disponível(is) no catálogo local.` });
    } catch (e) {
      setMensagem({ tipo: 'erro', texto: e instanceof Error ? e.message : 'Não foi possível buscar os modelos.' });
    } finally {
      setOperacao(null);
    }
  }

  async function testar() {
    if (!edicao) return;
    const salvo = await salvar();
    if (!salvo) return;
    setOperacao('testar');
    setMensagem(null);
    try {
      const r = await testarProvedor(salvo.id);
      setMensagem({ tipo: 'sucesso', texto: r.mensagem });
    } catch (e) {
      setMensagem({ tipo: 'erro', texto: e instanceof Error ? e.message : 'O teste não pôde ser concluído.' });
    } finally {
      setOperacao(null);
    }
  }

  async function excluir() {
    if (!edicao) return;
    setConfirmarExclusao(false);
    setOperacao('excluir');
    try {
      await removerProvedor(edicao.id);
      const lista = provedores.filter((p) => p.id !== edicao.id);
      setProvedores(lista);
      setSelecionado(lista[0]?.id ?? null);
      setEdicao(lista[0] ? structuredClone(lista[0]) : null);
    } catch (e) {
      setMensagem({ tipo: 'erro', texto: e instanceof Error ? e.message : 'Não foi possível excluir.' });
    } finally {
      setOperacao(null);
    }
  }

  async function criar(preset: Preset) {
    setAdicionar(false);
    setOperacao('criar');
    try {
      const criado = await salvarProvedor({
        nome: preset.nome,
        tipo: preset.tipo,
        baseUrl: preset.baseUrl,
        ativo: true,
        autenticacao: preset.autenticacao,
        caminhoModelos: preset.caminhoModelos,
        caminhoGeracao: preset.caminhoGeracao,
        tempoLimiteMs: 30000,
        modelos: []
      });
      setProvedores((lista) => [...lista, criado]);
      escolher(criado);
      setMensagem({ tipo: 'sucesso', texto: 'Provedor adicionado. Informe a chave e busque os modelos.' });
    } catch (e) {
      setMensagem({ tipo: 'erro', texto: e instanceof Error ? e.message : 'Não foi possível adicionar o provedor.' });
    } finally {
      setOperacao(null);
    }
  }

  function alterar<K extends keyof ProvedorAdministrativo>(campo: K, valor: ProvedorAdministrativo[K]) {
    if (edicao) setEdicao({ ...edicao, [campo]: valor });
  }

  function alterarModelo(id: string, campos: Partial<ModeloAdministrativo>) {
    if (!edicao) return;
    let modelos = edicao.modelos.map((m) => (m.id === id ? { ...m, ...campos } : m));
    if (campos.padrao) modelos = modelos.map((m) => ({ ...m, padrao: m.id === id, liberado: m.id === id ? true : m.liberado }));
    setEdicao({ ...edicao, modelos });
  }

  function adicionarModeloManual() {
    const id = modeloManual.trim();
    if (!id || !edicao || edicao.modelos.some((m) => m.id === id)) return;
    const modelo: ModeloAdministrativo = {
      id,
      nome: id,
      protocolo: 'openai-chat',
      liberado: false,
      disponivel: true,
      manual: true,
      padrao: false
    };
    setEdicao({ ...edicao, modelos: [...edicao.modelos, modelo] });
    setModeloManual('');
  }

  const modelosFiltrados = useMemo(
    () =>
      ordenarModelos(edicao?.modelos ?? []).filter((m) =>
        `${m.nome} ${m.id}`.toLocaleLowerCase('pt-BR').includes(busca.toLocaleLowerCase('pt-BR'))
      ),
    [busca, edicao?.modelos]
  );
  const liberados = edicao?.modelos.filter((m) => m.liberado).length ?? 0;

  if (!liberado) {
    return (
      <main className="tela-administracao">
        <header className="cabecalho-administracao">
          <button type="button" className="botao-voltar" onClick={aoVoltar}>
            <Icone nome="voltar" /> Voltar ao jogo
          </button>
          <div className="marca-admin">
            <span>
              <Icone nome="coroa" tamanho={25} />
            </span>
            <div>
              <strong>XADREZ</strong>
              <small>ÁREA RESTRITA</small>
            </div>
          </div>
          <div className="selo-local">
            <i />
            <span>Protegido por senha</span>
          </div>
        </header>
        <section className="intro-administracao" style={{ justifyContent: 'center' }}>
          <form className="modal-confirmacao" style={{ position: 'relative', margin: '40px auto' }} onSubmit={entrar}>
            <span className="selo-modal">
              <Icone nome="escudo" tamanho={28} />
            </span>
            <h2>Entrar na administração</h2>
            <p>Provedores, modelos de linguagem e testes experimentais.</p>
            <label className="campo-regio" style={{ textAlign: 'left', width: '100%' }}>
              <span>Senha</span>
              <input
                type="password"
                autoComplete="current-password"
                value={senha}
                onChange={(e) => {
                  setSenha(e.target.value);
                  setErroSenha('');
                }}
                autoFocus
              />
            </label>
            {erroSenha && (
              <p role="alert" style={{ color: '#d4877e', margin: 0 }}>
                {erroSenha}
              </p>
            )}
            <div className="acoes-modal">
              <button type="button" className="botao-contorno" onClick={aoVoltar}>
                Cancelar
              </button>
              <button type="submit" className="botao-principal">
                Entrar
              </button>
            </div>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="tela-administracao">
      <header className="cabecalho-administracao">
        <button type="button" className="botao-voltar" onClick={aoVoltar}>
          <Icone nome="voltar" /> Voltar ao jogo
        </button>
        <div className="marca-admin">
          <span>
            <Icone nome="coroa" tamanho={25} />
          </span>
          <div>
            <strong>XADREZ</strong>
            <small>ADMINISTRAÇÃO</small>
          </div>
        </div>
        <div className="selo-local">
          <button type="button" className="botao-texto-discreto" onClick={sair}>
            Sair
          </button>
        </div>
      </header>

      <section className="intro-administracao">
        <div>
          <span>PORTAL INTERNO</span>
          <h1>Administração e laboratório</h1>
          <p>
            O menu público só mostra modos de jogo clássicos. Modelos de linguagem e benchmarks ficam aqui — ainda em
            teste, não são feature pública.
          </p>
        </div>
        <div className="aviso-seguranca">
          <Icone nome="escudo" />
          <p>
            <strong>Acesso protegido por senha.</strong>
            <br />
            Chaves de API ficam criptografadas no servidor.
          </p>
        </div>
      </section>

      {(aoAbrirLab || aoAbrirBenchessmark) && (
        <section className="lab-admin" aria-label="Laboratório de IA">
          <div className="titulo-secao-modos" style={{ marginBottom: '1rem' }}>
            <span>LABORATÓRIO · EM TESTE</span>
            <h2>Modelos de linguagem</h2>
          </div>
          <div className="lista-modos lab-lista">
            {aoAbrirLab && (
              <>
                <button type="button" className="faixa-modo" onClick={() => aoAbrirLab('modelo')}>
                  <span className="numero-modo">IA</span>
                  <span className="icone-modo">
                    <Icone nome="modelo" tamanho={24} />
                  </span>
                  <span className="texto-modo">
                    <small>Humano × LLM</small>
                    <strong>Jogador vs modelo</strong>
                    <em>Partida de teste contra um modelo liberado.</em>
                  </span>
                  <span className="abrir-modo">
                    Abrir <b>→</b>
                  </span>
                </button>
                <button type="button" className="faixa-modo" onClick={() => aoAbrirLab('duelo-modelos')}>
                  <span className="numero-modo">IA</span>
                  <span className="icone-modo">
                    <Icone nome="servidor" tamanho={24} />
                  </span>
                  <span className="texto-modo">
                    <small>LLM × LLM</small>
                    <strong>Duelo de modelos</strong>
                    <em>Dois modelos jogam um contra o outro.</em>
                  </span>
                  <span className="abrir-modo">
                    Abrir <b>→</b>
                  </span>
                </button>
                <button type="button" className="faixa-modo" onClick={() => aoAbrirLab('benchmark')}>
                  <span className="numero-modo">IA</span>
                  <span className="icone-modo">
                    <Icone nome="olho" tamanho={24} />
                  </span>
                  <span className="texto-modo">
                    <small>Série assistida</small>
                    <strong>Benchmark de partidas</strong>
                    <em>Série com placar entre motores ou modelos.</em>
                  </span>
                  <span className="abrir-modo">
                    Abrir <b>→</b>
                  </span>
                </button>
              </>
            )}
            {aoAbrirBenchessmark && (
              <button type="button" className="faixa-modo" onClick={aoAbrirBenchessmark}>
                <span className="numero-modo">IA</span>
                <span className="icone-modo">
                  <Icone nome="teste" tamanho={24} />
                </span>
                <span className="texto-modo">
                  <small>Qualidade de lances</small>
                  <strong>Benchessmark</strong>
                  <em>Compara modelos em posições fixas com nota do Stockfish.</em>
                </span>
                <span className="abrir-modo">
                  Abrir <b>→</b>
                </span>
              </button>
            )}
          </div>
        </section>
      )}

      <section className="intro-administracao" style={{ paddingTop: '24px' }}>
        <div>
          <span>PROVEDORES</span>
          <h1 style={{ fontSize: 'clamp(1.8rem,3vw,2.6rem)' }}>Conexões e catálogo</h1>
          <p>Conecte serviços, descubra modelos e liberte quais aparecem no laboratório.</p>
        </div>
      </section>

      <section className="corpo-administracao">
        <aside className="lista-provedores">
          <header>
            <span>PROVEDORES</span>
            <button type="button" onClick={() => setAdicionar(true)}>
              <Icone nome="adicionar" tamanho={17} /> Adicionar
            </button>
          </header>
          {carregando ? (
            <div className="carregando-admin">
              <i />
              <span>Carregando configurações…</span>
            </div>
          ) : provedores.length ? (
            provedores.map((p) => (
              <button
                type="button"
                key={p.id}
                className={selecionado === p.id ? 'provedor-item provedor-selecionado' : 'provedor-item'}
                onClick={() => escolher(p)}
              >
                <span className="icone-provedor">
                  <Icone nome={p.tipo === 'llamacpp' ? 'servidor' : p.tipo.startsWith('opencode') ? 'modelo' : 'chave'} tamanho={21} />
                </span>
                <span>
                  <strong>{p.nome}</strong>
                  <small>
                    {tipoNome[p.tipo]} · {p.chaveConfigurada || p.autenticacao === 'nenhuma' ? 'configurado' : 'sem chave'}
                  </small>
                </span>
                <i className={p.ativo ? 'estado-ligado' : 'estado-desligado'} />
              </button>
            ))
          ) : (
            <div className="lista-vazia">
              <p>Nenhum provedor configurado.</p>
            </div>
          )}
        </aside>
        <div className="editor-provedor">
          {edicao ? (
            <>
              <header className="topo-editor">
                <div>
                  <span>{tipoNome[edicao.tipo].toUpperCase()}</span>
                  <input aria-label="Nome do provedor" value={edicao.nome} onChange={(e) => alterar('nome', e.target.value)} />
                </div>
                <label className="interruptor-compacto">
                  <span>{edicao.ativo ? 'Ativo' : 'Desativado'}</span>
                  <input type="checkbox" checked={edicao.ativo} onChange={(e) => alterar('ativo', e.target.checked)} />
                  <i />
                </label>
              </header>
              {mensagem && (
                <div className={`mensagem-admin mensagem-${mensagem.tipo}`} role="status">
                  <Icone nome={mensagem.tipo === 'sucesso' ? 'teste' : 'escudo'} tamanho={19} />
                  <span>{mensagem.texto}</span>
                  <button type="button" onClick={() => setMensagem(null)} aria-label="Fechar mensagem">
                    <Icone nome="fechar" tamanho={15} />
                  </button>
                </div>
              )}
              <section className="secao-editor">
                <div className="titulo-secao-editor">
                  <span>01</span>
                  <div>
                    <h2>Conexão</h2>
                    <p>Endereço, autenticação e limites deste provedor.</p>
                  </div>
                </div>
                <div className="grade-campos">
                  <label className="campo-admin campo-largo">
                    <span>Endereço da API</span>
                    <input value={edicao.baseUrl} placeholder="https://…" onChange={(e) => alterar('baseUrl', e.target.value)} />
                  </label>
                  <label className="campo-admin">
                    <span>Autenticação</span>
                    <select value={edicao.autenticacao} onChange={(e) => alterar('autenticacao', e.target.value as TipoAutenticacao)}>
                      <option value="bearer">Chave Bearer</option>
                      <option value="x-api-key">Cabeçalho X-Api-Key</option>
                      <option value="nenhuma">Sem autenticação</option>
                    </select>
                  </label>
                  <label className="campo-admin">
                    <span>Tempo limite</span>
                    <div className="campo-sufixo">
                      <input
                        type="number"
                        min="3000"
                        max="120000"
                        step="1000"
                        value={edicao.tempoLimiteMs}
                        onChange={(e) => alterar('tempoLimiteMs', Number(e.target.value))}
                      />
                      <b>ms</b>
                    </div>
                  </label>
                  {edicao.autenticacao !== 'nenhuma' && (
                    <label className="campo-admin campo-largo">
                      <span>Chave de acesso</span>
                      <div className="campo-chave">
                        <Icone nome="chave" tamanho={18} />
                        <input
                          type="password"
                          autoComplete="new-password"
                          value={chave}
                          placeholder={edicao.chaveConfigurada ? `Protegida ${edicao.chaveMascarada ?? ''}` : 'Informe a chave deste provedor'}
                          onChange={(e) => setChave(e.target.value)}
                        />
                      </div>
                      <small>Ao salvar, o valor é criptografado e não volta a ser exibido.</small>
                    </label>
                  )}
                  <label className="campo-admin">
                    <span>Caminho dos modelos</span>
                    <input value={edicao.caminhoModelos} onChange={(e) => alterar('caminhoModelos', e.target.value)} />
                  </label>
                  <label className="campo-admin">
                    <span>Caminho de geração</span>
                    <input value={edicao.caminhoGeracao} onChange={(e) => alterar('caminhoGeracao', e.target.value)} />
                  </label>
                </div>
                <div className="acoes-conexao">
                  <button type="button" className="botao-principal" disabled={Boolean(operacao)} onClick={() => void salvar()}>
                    <Icone nome="salvar" tamanho={18} />
                    {operacao === 'salvar' ? 'Salvando…' : 'Salvar conexão'}
                  </button>
                  <button type="button" className="botao-contorno" disabled={Boolean(operacao)} onClick={() => void testar()}>
                    <Icone nome="teste" tamanho={18} />
                    {operacao === 'testar' ? 'Testando…' : 'Testar conexão'}
                  </button>
                  <button type="button" className="botao-contorno" disabled={Boolean(operacao)} onClick={() => void descobrir()}>
                    <Icone nome="atualizar" tamanho={18} />
                    {operacao === 'descobrir' ? 'Buscando…' : 'Buscar modelos'}
                  </button>
                </div>
              </section>
              <section className="secao-editor">
                <div className="titulo-secao-editor">
                  <span>02</span>
                  <div>
                    <h2>Catálogo e escopo</h2>
                    <p>
                      {edicao.modelos.length} modelo(s) no catálogo · {liberados} liberado(s) para o laboratório.
                    </p>
                  </div>
                </div>
                <div className="barra-modelos">
                  <label>
                    <Icone nome="olho" tamanho={18} />
                    <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar pelo nome ou identificador" />
                  </label>
                  <div className="adicionar-manual">
                    <input
                      value={modeloManual}
                      onChange={(e) => setModeloManual(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          adicionarModeloManual();
                        }
                      }}
                      placeholder="Identificador manual"
                    />
                    <button type="button" onClick={adicionarModeloManual}>
                      <Icone nome="adicionar" tamanho={16} /> Incluir
                    </button>
                  </div>
                </div>
                <div className="tabela-modelos">
                  <div className="cabecalho-tabela-modelos">
                    <span>Modelo</span>
                    <span>Protocolo</span>
                    <span>Mostrar no lab</span>
                    <span>Padrão</span>
                  </div>
                  {modelosFiltrados.length ? (
                    modelosFiltrados.map((modelo) => (
                      <div className="linha-modelo" key={modelo.id}>
                        <div>
                          <strong>{modelo.nome}</strong>
                          <code>{modelo.id}</code>
                          <small>
                            {modelo.manual ? 'Adicionado manualmente' : modelo.disponivel ? 'Descoberto no provedor' : 'Catálogo armazenado'}
                          </small>
                        </div>
                        <label>
                          <span className="rotulo-movel">Protocolo</span>
                          <select
                            value={modelo.protocolo}
                            onChange={(e) => alterarModelo(modelo.id, { protocolo: e.target.value as ProtocoloModelo })}
                          >
                            {Object.entries(protocoloNome).map(([id, nome]) => (
                              <option key={id} value={id}>
                                {nome}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="seletor-escopo">
                          <input
                            type="checkbox"
                            checked={modelo.liberado}
                            onChange={(e) =>
                              alterarModelo(modelo.id, {
                                liberado: e.target.checked,
                                padrao: e.target.checked ? modelo.padrao : false
                              })
                            }
                          />
                          <i />
                          <span>{modelo.liberado ? 'Liberado' : 'Somente administração'}</span>
                        </label>
                        <label className="radio-padrao">
                          <input
                            type="radio"
                            name="padrao"
                            checked={modelo.padrao}
                            disabled={!modelo.liberado}
                            onChange={() => alterarModelo(modelo.id, { padrao: true })}
                          />
                          <i />
                          <span>Padrão</span>
                        </label>
                      </div>
                    ))
                  ) : (
                    <div className="modelos-vazios">
                      <Icone nome="modelo" tamanho={34} />
                      <strong>Nenhum modelo nesta lista</strong>
                      <p>Use “Buscar modelos” ou informe um identificador manualmente.</p>
                    </div>
                  )}
                </div>
                <footer className="rodape-editor">
                  <button type="button" className="botao-principal" disabled={Boolean(operacao)} onClick={() => void salvar()}>
                    <Icone nome="salvar" tamanho={18} />
                    Salvar catálogo
                  </button>
                  {edicao.tipo === 'personalizado' && (
                    <button type="button" className="botao-excluir" onClick={() => setConfirmarExclusao(true)}>
                      <Icone nome="lixeira" tamanho={18} />
                      Excluir provedor
                    </button>
                  )}
                </footer>
              </section>
            </>
          ) : (
            <div className="sem-selecao">
              <Icone nome="servidor" tamanho={42} />
              <h2>Escolha ou adicione um provedor</h2>
              <p>As configurações ficam guardadas apenas no servidor.</p>
            </div>
          )}
        </div>
      </section>

      {confirmarExclusao && edicao && (
        <div
          className="sobreposicao"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setConfirmarExclusao(false);
          }}
        >
          <section className="modal-confirmacao" role="alertdialog" aria-modal="true" aria-labelledby="titulo-exclusao">
            <span className="selo-modal">
              <Icone nome="lixeira" tamanho={28} />
            </span>
            <h2 id="titulo-exclusao">Excluir este provedor?</h2>
            <p>
              “{edicao.nome}” e seu catálogo serão removidos. Esta ação não pode ser desfeita.
            </p>
            <div className="acoes-modal">
              <button type="button" className="botao-contorno" onClick={() => setConfirmarExclusao(false)}>
                Cancelar
              </button>
              <button type="button" className="botao-excluir" disabled={Boolean(operacao)} onClick={() => void excluir()}>
                {operacao === 'excluir' ? 'Excluindo…' : 'Excluir provedor'}
              </button>
            </div>
          </section>
        </div>
      )}

      {adicionar && (
        <div
          className="sobreposicao"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setAdicionar(false);
          }}
        >
          <section className="modal-provedores" role="dialog" aria-modal="true" aria-labelledby="titulo-adicionar">
            <header>
              <div>
                <span>NOVA CONEXÃO</span>
                <h2 id="titulo-adicionar">Escolha um ponto de partida</h2>
                <p>Os endereços conhecidos já vêm preenchidos e continuam editáveis.</p>
              </div>
              <button type="button" className="botao-icone" onClick={() => setAdicionar(false)} aria-label="Fechar">
                <Icone nome="fechar" />
              </button>
            </header>
            <div className="grade-presets">
              {presets.map((p) => (
                <button type="button" key={p.tipo} onClick={() => void criar(p)}>
                  <span className="icone-preset">
                    <Icone nome={p.tipo === 'llamacpp' ? 'servidor' : p.tipo === 'personalizado' ? 'adicionar' : 'modelo'} tamanho={25} />
                  </span>
                  <span>
                    <strong>{p.nome}</strong>
                    <small>{p.descricao}</small>
                    <code>{p.baseUrl || 'Endereço definido por você'}</code>
                  </span>
                  <Icone nome="avancar" tamanho={18} />
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
